import React, { useState, useEffect } from "react";
import axiosInstance from "../AxiosInstance";
import SidebarLayout from "../SidebarLayout";
import VenueSearchForm from "./VenueSearchForm";
import VenueTable from "./VenueTable";
import VenuePagination from "./VenuePagination";
import VenueDialog from "./VenueDialog";
import { Container, Button } from "@mui/material";
import { useNotification } from "../NotificationContext";
import { saveAs } from "file-saver";
import { jwtDecode } from "jwt-decode";
import { throttle } from "lodash";
import { motion } from "framer-motion";

const VENUE_CACHE_KEY = "venuemain_state_v1";

const readVenueCache = () => {
  try {
    const raw = sessionStorage.getItem(VENUE_CACHE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const VenueMain = () => {
  const cached = readVenueCache();
  const [userRole, setUserRole] = useState(null);
  const [icpisname, setIcpisname] = useState("");
  const [isDisabled, setIsDisabled] = useState(false);
  const [timeZone, setTimeZone] = useState(cached.timeZone ?? "");
  const [state, setState] = useState(cached.state ?? "");
  const [states, setStates] = useState([]);
  const [instructor, setInstructor] = useState(cached.instructor ?? "");
  const [icpisManagers, setIcpisManagers] = useState([]);
  const [icpisManager, setIcpisManager] = useState(cached.icpisManager ?? "");
  const [venues, setVenues] = useState(Array.isArray(cached.venues) ? cached.venues : []);
  const [totalVenues, setTotalVenues] = useState(cached.totalVenues || 0);
  const [currentPage, setCurrentPage] = useState(cached.currentPage || 1);
  const [itemsPerPage] = useState(10); // 每页显示1个item
  const [currentVenue, setCurrentVenue] = useState({});
  const [originalVenueId, setOriginalVenueId] = useState(""); // 保存原始 Venue ID
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const timeZones = ["PST", "EST", "CST", "MST", "GMT", "UTC", "AKST"];
  const { showNotification } = useNotification();
  const [totalPages, setTotalPages] = useState(cached.totalPages || 1); // 添加 totalPages 状态
  const [selectedFile, setSelectedFile] = useState(null);
  const [venueId, setVenueId] = useState(cached.venueId ?? "");
  const [instructors, setInstructors] = useState([]); // 新增 instructors 状态
  const [city, setCity] = useState(cached.city ?? "");
  const [venueStatus, setVenueStatus] = useState(cached.venueStatus ?? "");
  const refreshPageAfterEdit = () => {
    window.location.reload();
  };
  // const token = localStorage.getItem("token");
  //     let userRole = null;
  //     let icpisname=null;
  //     if (token) {
  //     const decodedToken = jwtDecode(token);
  //     userRole = decodedToken.claims.role;
  //     icpisname=decodedToken.claims.firstname;
  //   }
  // useEffect(() => {
  //   fetchInstructors();
  //   fetchVenues();
  //   handleSearch();
  //   fetchIcpisManagers();
  //   fetchStates();
  // }, [currentPage]);
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        console.log("Decoded Token:", decodedToken);
        setUserRole(decodedToken.claims.role);
        setIcpisname(decodedToken.claims.icpiename);
      } catch (error) {
        console.error("Invalid token:", error);
      }
    }
  }, []);

  useEffect(() => {
    if (!userRole) return;

    handleSearch();
    fetchStates();
    fetchInstructors();

    if (userRole === "ROLE_ICPIE") {
      fetchIcpisManagers();
    }
  }, [currentPage, userRole]);

  useEffect(() => {
    try {
      sessionStorage.setItem(
        VENUE_CACHE_KEY,
        JSON.stringify({
          timeZone,
          state,
          instructor,
          icpisManager,
          venues,
          totalVenues,
          currentPage,
          totalPages,
          venueId,
          city,
          venueStatus,
        }),
      );
    } catch {
      // ignore cache write errors
    }
  }, [
    timeZone,
    state,
    instructor,
    icpisManager,
    venues,
    totalVenues,
    currentPage,
    totalPages,
    venueId,
    city,
    venueStatus,
  ]);
  // const fetchIcpisManagers=async()=>{
  //   try{
  //     const response=await axiosInstance.get('/icpis/icpismanagers');
  //     setIcpisManagers(response.data);

  //   }
  //   catch(error){
  //     console.error('Error fetching icpismanagers:',error);
  //   }

  // };
  const fetchInstructors = async () => {
    try {
      const token = localStorage.getItem("token");
      //("Stored JWT in localStorage:", token);

      const authHeader = "Bearer " + token;
      // 判断 userRole 是否是 'ROLE_ICPIE'
      let queryParams = {};
      if (userRole !== "ROLE_ICPIE") {
        queryParams = {
          role: userRole,
          icpisName: icpisname,
        };
      } else {
        queryParams = { role: userRole }; // ROLE_ICPIE 也应该有 role 参数
      }
      const response = await axiosInstance.get("/instructor/instructorname", {
        params: queryParams,
        headers: {
          Authorization: authHeader,
        },
        // 传递参数
      });
      setInstructors(response.data); // 设置 instructors 数据
    } catch (error) {
      console.error("Error fetching instructors:", error);
    }
  };
  //获取state数据

  const fetchStates = async () => {
    try {
      const response = await axiosInstance.get("/venue/allstates", {
        params: {
          role: userRole,
          icpisName: icpisname,
        },
      });
      const data = Array.isArray(response.data) ? [...response.data] : [];
      data.sort();
      setStates(data);
    } catch (error) {
      console.error("Error fetching states:", error);
    }
  };

  //获取IcpisManager
  const fetchIcpisManagers = async () => {
    if (userRole === "ROLE_ICPIE") {
      try {
        const response = await axiosInstance.get("/icpie/allicpiamanger");
        setIcpisManagers(response.data);
      } catch (error) {
        console.error("Error fetching", error);
      }
    }
  };

  //获取venues信息
  const fetchVenues = async () => {
    if (!userRole) return;
    try {
      const token = localStorage.getItem("token");
      //("Stored JWT in localStorage:", token);

      const authHeader = "Bearer " + token;
      const response = await axiosInstance.get("/venue", {
        params: {
          pageNum: currentPage,
          pageSize: itemsPerPage,
          icpisname: icpisname,
          role: userRole,
          state,
          city: city,
          instructor: instructor,
          icpisManager: icpisManager,
          timeZone: timeZone,
          venueStatus: venueStatus,
        },
        headers: {
          Authorization: authHeader, // 传递 Bearer token
        },
      });
      if (response.data.message === "success") {
        setVenues(response.data.data.items);
      } else {
        showNotification("Get Venue information failed!", "error");
      }
    } catch (error) {
      console.error("Get Venue information failed:", error);
    }
  };
  //搜索venues信息
  const handleSearch = (forcePage = null, overrideFilters = {}) => {
    if (isLoading) return;
    setIsLoading(true);
    const pageToFetch = forcePage !== null ? forcePage : currentPage;
    if (forcePage !== null) {
      setCurrentPage(forcePage);
    }

    let params = {
      pageNum: pageToFetch,
      pageSize: itemsPerPage,
      icpisname: icpisname,
      role: userRole,
      state,
      city: city,
      icpisManager: icpisManager,
      timeZone: timeZone,
      venueId: venueId,
      venueStatus: venueStatus,
      ...overrideFilters,
    };

    Object.keys(params).forEach((key) => {
      if (
        params[key] === "" ||
        params[key] === null ||
        params[key] === undefined
      ) {
        delete params[key];
      }
    });
    const token = localStorage.getItem("token");
    //("Stored JWT in localStorage:", token);

    const authHeader = "Bearer " + token;
    axiosInstance
      .get("/venue", {
        params: params, // 将 params 正确放入第二个参数中的 params 字段
        headers: {
          Authorization: authHeader, // 传递 Bearer token
        },
      })
      .then((response) => {
        if (response.data.message === "success") {
          console.log("Full Response:", response.data);

          // 处理 venues 数据
          const venues = response.data.data.items.map((venue) => {
            // 直接使用返回的 instructors 列表
            return {
              ...venue,
              instructors: venue.instructors || [], // 如果没有 instructors 则设置为空数组
            };
          });

          // 更新状态
          setVenues(venues);
          showNotification(
            "Venues information searched successfully!",
            "success",
          );
          setTotalVenues(response.data.totalElements);
          setTotalPages(
            Math.ceil(response.data.data.totalElement / itemsPerPage),
          );
        } else {
          showNotification("Venues information searched failed!", "error");
        }
      })
      .catch((error) => {
        console.error("Error:", error);
      })
      .finally(() => {
        setIsLoading(false);
        //console.log(`totalpages,${totalPages}`);
      });
  };
  const handleAdd = () => {
    const newVenue = {
      id: "",
      state: "",
      city: "",
      address: "",
      timeZone: "",
      cancellationPolicy: "",
      paymentMode: "",
      nonrefundableFee: 0,
      fobKey: "",
      deposit: "",
      membershipFee: 0,
      usageFee: 0,
      refundableStatus: "",
      bookMethod: "",
      registrationLink: "",
      icpisManager: userRole === "ROLE_ICPIE" ? "" : icpisname,
      cprPrice: 0,
      blsPrice: 0,
      cpradultPrice: 0,
      cprinstructorPrice: 0,
      cprskillPrice: 0,
      instructor: [],
      venueStatus: "NORMAL",
      adFrequencyValue: 2,
      adFrequencyReason: "",
      site: null,
    };

    setCurrentVenue(newVenue);
    setOriginalVenueId(""); // 新建时没有原始 ID
    setIsEditMode(false);
    setOpen(true);
  };

  const handleClickOpen = (venue) => {
    setCurrentVenue(venue);
    setOriginalVenueId(venue.id); // 保存原始 ID
    setOpen(true);
    setIsEditMode(true);
  };
  //关闭
  const handleClose = () => {
    setOpen(false);
  };
  //变化
  const handleChange = (event) => {
    const { name, value } = event.target;

    if (name === "site") {
      const trimmed = String(value).trim();
      const n = trimmed === "" ? null : Number(trimmed);
      setCurrentVenue({
        ...currentVenue,
        site: trimmed === "" || !Number.isFinite(n) ? null : n,
      });
      return;
    }

    // 如果是 instructors 列表，更新 currentVenue.instructors
    if (name === "instructors") {
      const selectedInstructors = value.map((id) =>
        instructors.find((i) => i.id === id),
      );
      setCurrentVenue({
        ...currentVenue,
        instructors: selectedInstructors, // 更新为用户选择的 instructor ID 列表
      });
    } else {
      setCurrentVenue({
        ...currentVenue,
        [name]: value, // 更新其他字段
      });
    }
  };
  //保存
  const handleSave = throttle(async () => {
    if (isDisabled) return;
    setIsDisabled(true);

    // 检查是否修改了 Venue ID
    const isIdChanged = originalVenueId && currentVenue.id !== originalVenueId;

    // 如果 ID 发生变化，先弹出确认框
    if (isIdChanged) {
      const confirmed = window.confirm(
        `Are you sure you want to change the Venue ID from "${originalVenueId}" to "${currentVenue.id}"?\n\n` +
          `This will update all related records:\n` +
          `- Instructor associations\n` +
          `- Venue schedules\n` +
          `- Course schedules\n\n` +
          `This action cannot be undone.`,
      );

      if (!confirmed) {
        setIsDisabled(false);
        return;
      }

      // 先执行 ID 更新
      try {
        const idUpdateResponse = await axiosInstance.put(
          `/venue/updateVenueId?oldId=${encodeURIComponent(originalVenueId)}&newId=${encodeURIComponent(currentVenue.id)}`,
        );

        if (idUpdateResponse.data.code !== 0) {
          showNotification(
            idUpdateResponse.data.message || "Failed to update Venue ID",
            "error",
          );
          setIsDisabled(false);
          return;
        }

        showNotification(
          `Venue ID updated from ${originalVenueId} to ${currentVenue.id}`,
          "success",
        );
        // 更新 originalVenueId 为新的 ID
        setOriginalVenueId(currentVenue.id);
      } catch (error) {
        console.error("Error updating Venue ID:", error);
        showNotification(
          "Failed to update Venue ID: " +
            (error.response?.data?.message || error.message),
          "error",
        );
        setIsDisabled(false);
        return;
      }
    }

    // 从 currentVenue 中提取 instructor 对象，并生成 instructorIds 列表
    const instructorIds = Array.isArray(currentVenue.instructors)
      ? currentVenue.instructors
          .map((instructor) => instructor && instructor.id)
          .filter((id) => id != null && String(id).trim() !== "")
      : []; // 提取 ID（过滤无效项，避免与库中关联集合误判为不一致而触发全量 sync / Slack）
    const venueStatus = currentVenue.venueStatus;
    const icpisManager = currentVenue.icpisManager;
    const address = currentVenue.address;
    const timezone = currentVenue.timeZone;
    const state = currentVenue.state;
    const city = currentVenue.city;
    const cancellationpolicy = currentVenue.cancellationPolicy;
    const paymentmode = currentVenue.paymentMode;
    const nonrefundablefee = currentVenue.nonrefundableFee;
    const fobkey = currentVenue.fobKey;
    const deposit = currentVenue.deposit;
    const membershipfee = currentVenue.membershipFee;
    const usageFee = currentVenue.usageFee;
    const refundablestatus = currentVenue.refundableStatus;
    const bookmethod = currentVenue.bookMethod;
    const registrationlink = currentVenue.registrationLink;
    const cprPrice = currentVenue.cprPrice;
    const blsPrice = currentVenue.blsPrice;
    const cpradultPrice = currentVenue.cpradultPrice;
    const cprinstructorPrice = currentVenue.cprinstructorPrice;
    const cprskillPrice = currentVenue.cprskillPrice;
    const adFrequencyValue = currentVenue.adFrequencyValue;
    const adFrequencyReason = currentVenue.adFrequencyReason;
    // 构建符合 VenueDTO 格式的对象
    const venueDTO = {
      ...currentVenue,
      id: currentVenue.id, // 使用新的 ID
      instructorIds: instructorIds, // 替换 instructor 对象为 ID 列表
      venueStatus: venueStatus,
      icpisManager: icpisManager,
      address: address,
      timeZone: timezone,
      state: state,
      city: city,
      cancellationPolicy: cancellationpolicy,
      paymentMode: paymentmode,
      nonrefundableFee: nonrefundablefee,
      fobKey: fobkey,
      deposit: deposit,
      membershipFee: membershipfee,
      usageFee: usageFee,
      refundableStatus: refundablestatus,
      bookMethod: bookmethod,
      registrationLink: registrationlink,
      cprPrice: cprPrice,
      blsPrice: blsPrice,
      cpradultPrice: cpradultPrice,
      cprinstructorPrice: cprinstructorPrice,
      cprskillPrice: cprskillPrice,
      adFrequencyReason: adFrequencyReason,
      adFrequencyValue: adFrequencyValue,
    };
    //console.log(JSON.stringify(venueDTO, null, 2));
    //console.log('Venue DTO:', venueDTO);  // 调试时查看传递的 DTO 数据
    //console.log("currentVenue.instructors:", currentVenue.icpisManager);  // 调试 instructors 原始数据

    // 发送请求
    axiosInstance
      .put("/venue", venueDTO, {
        headers: {
          "Content-Type": "application/json",
        },
      })
      .then((response) => {
        if (response?.data?.code !== 0) {
          setIsDisabled(false);
          showNotification(
            response?.data?.message || "Venues information updated failed!",
            "error",
          );
          return;
        }
        // 如果 ID 变化了，需要用新 ID 替换旧记录
        if (isIdChanged) {
          setVenues((prev) =>
            prev.map((v) =>
              v.id === originalVenueId
                ? { ...currentVenue, id: currentVenue.id }
                : v,
            ),
          );
        } else {
          setVenues((prev) =>
            prev.map((v) => (v.id === currentVenue.id ? currentVenue : v)),
          );
        }
        setOpen(false);
        setIsDisabled(false);
        showNotification("Venues information updated successfully!", "success");
        refreshPageAfterEdit();
      })
      .catch((error) => {
        // 处理错误
        setIsDisabled(false);
        showNotification(
          error?.response?.data?.message || "Network error while updating venue",
          "error",
        );
      });
  }, 6000);
  const handleInsert = () => {
    // 从 currentVenue 获取讲师的 id 列表，如果讲师为空则返回空数组
    const instructorIds = Array.isArray(currentVenue.instructors)
      ? currentVenue.instructors
          .map((instructor) => instructor && instructor.id)
          .filter((id) => id != null && String(id).trim() !== "")
      : [];

    // 创建要发送的请求数据
    const data = {
      id: currentVenue.id,
      venueStatus: currentVenue.venueStatus,
      icpisManager: currentVenue.icpisManager,
      address: currentVenue.address,
      cancellationPolicy: currentVenue.cancellationPolicy,
      paymentMode: currentVenue.paymentMode,
      nonrefundableFee: currentVenue.nonrefundableFee,
      fobKey: currentVenue.fobKey,
      deposit: currentVenue.deposit,
      membershipFee: currentVenue.membershipFee,
      usageFee: currentVenue.usageFee,
      refundableStatus: currentVenue.refundableStatus,
      bookMethod: currentVenue.bookMethod,
      registrationLink: currentVenue.registrationLink,
      city: currentVenue.city,
      site: currentVenue.site,
      timeZone: currentVenue.timeZone || "PST",
      state: currentVenue.state,
      instructorIds: instructorIds, // 添加讲师 ID 列表
      cprPrice: currentVenue.cprPrice,
      blsPrice: currentVenue.blsPrice,
      cpradultPrice: currentVenue.cpradultPrice,
      cprinstructorPrice: currentVenue.cprinstructorPrice,
      cprskillPrice: currentVenue.cprskillPrice,
      adFrequencyReason: currentVenue.adFrequencyReason,
      adFrequencyValue: currentVenue.adFrequencyValue,
    };

    console.log("新场地信息是：", data);

    // 发送请求
    axiosInstance
      .post("/venue", data, {
        headers: {
          "Content-Type": "application/json",
        },
      })
      .then((response) => {
        setOpen(false);
        showNotification("Add a new venue successfully", "success");
      })
      .catch((error) => {
        console.error("Bad Request:", error);
      });
  };
  //删除场地
  const handleDelete = () => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this venue? This action cannot be undone.",
    );
    if (confirmDelete) {
      axiosInstance
        .delete("/venue", { params: { venueId: currentVenue.id } })
        .then((response) => {
          setOpen(false);
          showNotification("Venue deleted successfully!", "success");
        })
        .catch((error) => {
          console.error("bad request", error);
        });
    }
  };
  //重置搜索条件
  const handleReset = () => {
    setTimeZone("");
    setState("");
    setInstructor("");
    setCity("");
    setVenueStatus("");
    setVenues([]);
    setCurrentPage(1);
    setIcpisManager("");
    setVenueId("");
    showNotification("Reset successfully!", "success");
    handleSearch(1, {
      state: "",
      city: "",
      icpisManager: "",
      timeZone: "",
      venueId: "",
      venueStatus: "",
    });
  };
  //翻页
  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    //console.log(`Page change triggered: ${value}`);
  };

  //导出文件
  const handleExport = async () => {
    try {
      const response = await axiosInstance.get("/venue/export", {
        responseType: "blob",
      });

      const contentDisposition = (await response).headers[
        "content-disposition"
      ];
      const filename = contentDisposition
        ? contentDisposition
            .split("filename")[1]
            .split(";")[0]
            .replace(/"/g, "")
        : "venues.xlsx";

      //console.log('Content-Disposition:', contentDisposition);

      //文件下载
      saveAs(new Blob([response.data]), filename);
    } catch (error) {
      console.error("Error exported data", error);
    }
  };

  //const totalPages = Math.ceil(totalVenues/ itemsPerPage);

  //上传文件到后端
  const handleFileChange = (event) => {
    setSelectedFile(event.target.files[0]);
    event.target.value = null;
  };

  const handleUpload = () => {
    if (!selectedFile) {
      showNotification("Please select a file first!", "error");
      return;
    }
    const formData = new FormData();
    formData.append("file", selectedFile);

    axiosInstance
      .post("/venue/import", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then((response) => {
        //console.log(response.data);
      })
      .catch((error) => {
        //console.log(error);
      });
  };

  return (
    <SidebarLayout>
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -50 }}
        transition={{ duration: 1.0 }}
      >
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
          <VenueSearchForm
            timeZone={timeZone}
            state={state}
            instructor={instructor}
            icpisManager={icpisManager}
            setTimeZone={setTimeZone}
            setState={setState}
            setInstructor={setInstructor}
            handleSearch={handleSearch}
            handleReset={handleReset}
            handleExport={handleExport}
            handleFileChange={handleFileChange}
            handleUpload={handleUpload}
            selectedFile={selectedFile}
            setIcpisManager={setIcpisManager}
            venueId={venueId}
            userRole={userRole}
            setVenueId={setVenueId}
            venueStatus={venueStatus}
            setVenueStatus={setVenueStatus}
            city={city}
            setCity={setCity}
            states={states}
            icpisManagers={icpisManagers}
          />
          <VenueTable currentItems={venues} handleClickOpen={handleClickOpen} />
          <VenuePagination
            totalPages={totalPages}
            currentPage={currentPage}
            handlePageChange={handlePageChange}
          />
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.9 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleAdd}
              sx={{ mt: 2 }}
            >
              Add a new venue
            </Button>
          </motion.div>
        </Container>
        <VenueDialog
          userRole={userRole}
          icpisname={icpisname}
          open={open}
          handleClose={handleClose}
          isEditMode={isEditMode}
          currentVenue={currentVenue}
          originalVenueId={originalVenueId}
          handleChange={handleChange}
          handleSave={handleSave}
          handleInsert={handleInsert}
          handleDelete={handleDelete}
          timeZones={timeZones}
          instructors={instructors}
          icpisManager={icpisManager}
          isDisabled={isDisabled}
        />
      </motion.div>
    </SidebarLayout>
  );
};

export default VenueMain;
